import React, { useState, useEffect, useRef } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import FeaturedCollection from './components/FeaturedCollection';
import GridCategories from './components/GridCategories';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import VideoSection from './components/VideoSection';
import Contact from './components/Contact';
import Concierge from './components/Concierge';
import Services from './components/Services';
import About from './components/About';
import Auth from './components/Auth';
import Shop from './components/Shop';
import Checkout from './components/Checkout';
import AdminDashboard from './components/AdminDashboard';
import AdminProducts from './components/AdminProducts';
import AdminPricing from './components/AdminPricing';
import AdminProfile from './components/AdminProfile';
import AdminSettings from './components/AdminSettings';
import AdminMembers from './components/AdminMembers';
import AdminTransactions from './components/AdminTransactions';
import MemberProfile from './components/MemberProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import ProductDetailModal from './components/ProductDetailModal';
import AuthRequired from './components/AuthRequired';
import ResetPassword from './components/ResetPassword';
import PaymentProofUpload from './components/PaymentProofUpload';
import AdminBankDetails from './components/AdminBankDetails';
import Wishlist from './components/Wishlist';
import { User, Product, GoldRate, SiteSettings, Member, WishlistItem, CartItem, MemberOrder } from './types';

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

// Define types for API responses
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  count?: number;
  source?: string;
  debug?: string[];
}

interface SettingsItem {
  setting_key: string;
  setting_value: string;
}

// Default site settings (fallback)
const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteName: 'Sparkling Gems',
  siteDescription: 'Luxury jewelry and accessories',
  contactEmail: 'rsgleamofgolds@gmail.com',
  contactPhone: '0945 392 8946',
  address: '2F, San Antonio Building, Corumi, Quezon City, 1012 Metro Manila',
  currency: 'PHP',
  taxRate: 0.12,
  email: 'rsgleamofgolds@gmail.com',
  phone: '0945 392 8946',
  hours: 'Mon-Sun: 9am - 8pm',
  facebook_url: 'https://www.facebook.com/sparkling1gems',
  instagram_url: 'https://www.instagram.com/sparkling1gems',
  shipping_fee: 0,
  free_shipping_threshold: 5000
};

// Use more reliable placeholder service
const PLACEHOLDER_IMAGE = 'https://placehold.co/400x400/f5f5f5/999999?text=No+Image';

// ============================================================
// FIXED: getCleanImageUrl - KEEPS all valid images, including base64
// ============================================================
const getCleanImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl || typeof imageUrl !== 'string') return '';

  const trimmed = imageUrl.trim();
  if (!trimmed) return '';

  // Keep valid base64 images (these are legitimate uploaded images)
  if (trimmed.startsWith('data:image/')) {
    // Fix malformed base64 if needed
    let fixed = trimmed;
    if (fixed.includes(':base64') && !fixed.includes(';base64')) {
      fixed = fixed.replace(':base64', ';base64,');
    }
    if (fixed.includes(';base64') && !fixed.includes(';base64,')) {
      fixed = fixed.replace(';base64', ';base64,');
    }
    return fixed;
  }

  // Keep blob URLs (temporary URLs from file selection)
  if (trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // Keep full absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Keep protocol-relative URLs
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  // Convert relative paths to absolute URLs
  if (trimmed.startsWith('/')) {
    const SITE_BASE_URL = process.env.NODE_ENV === 'production' 
      ? 'https://sparklinggems.shop'
      : 'http://localhost/sparkling-gems-backend-php';
    return `${SITE_BASE_URL}${trimmed}`;
  }

  // Default: treat as relative path from root
  const SITE_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://sparklinggems.shop'
    : 'http://localhost/sparkling-gems-backend-php';
  return `${SITE_BASE_URL}/${trimmed}`;
};

// Helper functions for tier calculation
const calculatePointsFromSpent = (totalSpent: number): number => {
  return Math.floor(totalSpent / 100);
};

const getTierFromPoints = (points: number): 'Sapphire' | 'Ruby' | 'Emerald' | 'Diamond' => {
  if (points >= 50000) return 'Diamond';
  if (points >= 15000) return 'Emerald';
  if (points >= 5000) return 'Ruby';
  return 'Sapphire';
};

// Helper function to safely parse weight
const parseWeight = (weight: string | number | undefined): number => {
  if (weight === undefined || weight === null) return NaN;
  
  let weightStr: string;
  if (typeof weight === 'number') {
    weightStr = weight.toString();
  } else {
    weightStr = weight;
  }
  
  // Remove 'g' suffix and any other non-numeric characters except decimal point
  const cleaned = weightStr.replace(/[^0-9.]/g, '');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? NaN : parsed;
};

function App() {
  const [activePage, setActivePage] = useState('home');
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('sparklingUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [rates, setRates] = useState<GoldRate[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [members, setMembers] = useState<Member[]>([]);
  
  // Global Order State
  const [allOrders, setAllOrders] = useState<MemberOrder[]>([]);
  
  // Wishlist, Cart & Modal State
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // State for direct checkout from product modal or wishlist
  const [directCheckoutItem, setDirectCheckoutItem] = useState<{ product: Product; quantity: number } | null>(null);
  const [wishlistCheckoutItems, setWishlistCheckoutItems] = useState<CartItem[] | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [dataSources, setDataSources] = useState<Record<string, string>>({});
  
  // Image error tracking
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Cart and Wishlist counts for navbar
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  // Overlay states for cart and wishlist
  const [showCart, setShowCart] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);

  // Prevent duplicate wishlist clicks per product
  const wishlistProcessingRef = useRef<Set<number>>(new Set());

  // URL to Page mapping
  const pathToPage: Record<string, string> = {
    '/': 'home',
    '/home': 'home',
    '/shop': 'shop',
    '/collections': 'collections',
    '/bags': 'bags',
    '/fragrances': 'fragrances',
    '/checkout': 'checkout',
    '/contact': 'contact',
    '/services': 'services',
    '/about': 'about',
    '/privacy-policy': 'privacy-policy',
    '/auth': 'auth',
    '/register': 'register',
    '/reset-password': 'reset-password',
    '/profile': 'profile',
    '/orders': 'orders',
    '/upload-proof': 'upload-proof',
    '/admin/dashboard': 'admin-dashboard',
    '/admin/products': 'admin-products',
    '/admin/add-product': 'admin-add-product',
    '/admin/pricing': 'admin-pricing',
    '/admin/profile': 'admin-profile',
    '/admin/settings': 'admin-settings',
    '/admin/members': 'admin-members',
    '/admin/transactions': 'admin-transactions',
    '/admin/bank-details': 'admin-bank-details',
    '/admin-login': 'admin-portal',
    '/login-admin': 'admin-portal',
  };

  // Initialize page from URL on load
  useEffect(() => {
    const path = window.location.pathname;
    const savedPage = localStorage.getItem('sparklingActivePage');
    
    // Check if it's an admin route with /admin/ prefix
    if (path.startsWith('/admin/')) {
      const adminPage = path.replace('/admin/', '');
      const pageMap: Record<string, string> = {
        'dashboard': 'admin-dashboard',
        'products': 'admin-products',
        'add-product': 'admin-add-product',
        'pricing': 'admin-pricing',
        'profile': 'admin-profile',
        'settings': 'admin-settings',
        'members': 'admin-members',
        'transactions': 'admin-transactions',
        'bank-details': 'admin-bank-details'
      };
      if (pageMap[adminPage]) {
        setActivePage(pageMap[adminPage]);
        return;
      }
    }
    
    // Check for direct matches
    if (pathToPage[path]) {
      setActivePage(pathToPage[path]);
    } 
    // Check for saved admin page
    else if (savedPage && savedPage.startsWith('admin-')) {
      setActivePage(savedPage);
      // Update URL to match
      const adminPath = savedPage.replace('admin-', '');
      window.history.replaceState(null, '', `/admin/${adminPath}`);
    }
  }, []);

  // Update URL when activePage changes
  useEffect(() => {
    // Save to localStorage for persistence across reloads
    localStorage.setItem('sparklingActivePage', activePage);
    
    // Update URL based on page
    const pageToPath: Record<string, string> = {
      'home': '/',
      'shop': '/shop',
      'collections': '/collections',
      'bags': '/bags',
      'fragrances': '/fragrances',
      'checkout': '/checkout',
      'contact': '/contact',
      'services': '/services',
      'about': '/about',
      'privacy-policy': '/privacy-policy',
      'auth': '/auth',
      'register': '/register',
      'reset-password': '/reset-password',
      'profile': '/profile',
      'orders': '/orders',
      'upload-proof': '/upload-proof',
      'admin-dashboard': '/admin/dashboard',
      'admin-products': '/admin/products',
      'admin-add-product': '/admin/add-product',
      'admin-pricing': '/admin/pricing',
      'admin-profile': '/admin/profile',
      'admin-settings': '/admin/settings',
      'admin-members': '/admin/members',
      'admin-transactions': '/admin/transactions',
      'admin-bank-details': '/admin/bank-details',
      'admin-portal': '/admin-login',
    };

    const path = pageToPath[activePage];
    if (path && window.location.pathname !== path) {
      window.history.pushState(null, '', path);
    }
  }, [activePage]);

  // Update counts when cart/wishlist change
  useEffect(() => {
    setCartCount(cart.length);
  }, [cart]);

  useEffect(() => {
    setWishlistCount(wishlist.length);
  }, [wishlist]);

  // Listen for storage events from Cart/Wishlist components
  useEffect(() => {
    const handleStorageChange = () => {
      // Refresh cart and wishlist when localStorage changes
      loadWishlistFromDatabase();
      loadCartFromDatabase();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for cartCleared event
  useEffect(() => {
    const handleCartCleared = () => {
      console.log('Cart cleared event received, reloading cart...');
      loadCartFromDatabase();
      setDirectCheckoutItem(null);
      setWishlistCheckoutItems(null);
    };
    
    window.addEventListener('cartCleared', handleCartCleared);
    return () => window.removeEventListener('cartCleared', handleCartCleared);
  }, []);

  // Listen for wishlist-updated event
  useEffect(() => {
    const handleWishlistUpdated = () => {
      console.log('Wishlist updated event received, reloading wishlist...');
      loadWishlistFromDatabase();
    };
    
    window.addEventListener('wishlist-updated', handleWishlistUpdated);
    return () => window.removeEventListener('wishlist-updated', handleWishlistUpdated);
  }, []);

  // Handle wishlist checkout data when navigating to checkout
  useEffect(() => {
    if (activePage === 'checkout') {
      const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
      if (wishlistCheckout) {
        try {
          const data = JSON.parse(wishlistCheckout);
          if (data.source === 'wishlist' && data.items && data.items.length > 0) {
            console.log('Processing wishlist checkout with', data.items.length, 'items');
            
            // Convert wishlist items to cart items for checkout
            const checkoutItems: CartItem[] = data.items.map((item: any) => ({
              id: item.product.id,
              cart_id: item.product.id,
              name: item.product.name,
              price: item.product.price,
              image: item.product.image,
              category: item.product.category,
              description: item.product.description,
              quantity: item.quantity,
              selected: true,
              stock: item.product.stock,
              total: item.product.price * item.quantity
            }));
            
            setWishlistCheckoutItems(checkoutItems);
          }
        } catch (e) {
          console.error('Error parsing wishlist checkout:', e);
        }
      }
    } else {
      // Clear wishlist checkout items when leaving checkout
      setWishlistCheckoutItems(null);
    }
  }, [activePage]);

  // Fetch all data on app load
  useEffect(() => {
    const loadAllData = async () => {
      console.log('Starting to load all data...');
      await fetchProductsFromDatabase();
      await fetchGoldRatesFromDatabase();
      await fetchSiteSettingsFromDatabase();
      await fetchMembersFromDatabase();
      
      // Load wishlist and cart after products are loaded
      console.log('Products loaded, now loading wishlist and cart...');
      loadWishlistFromDatabase();
      loadCartFromDatabase();
      
      // Fetch orders AFTER user is loaded
      await fetchOrdersFromDatabase();
    };
    
    loadAllData();
  }, []);

  // Reload wishlist, cart, and orders when user changes
  useEffect(() => {
    if (products.length > 0) {
      console.log('👤 User changed, reloading user data...');
      loadWishlistFromDatabase();
      loadCartFromDatabase();
      fetchOrdersFromDatabase();
    }
  }, [user]);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('sparklingUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('sparklingUser');
    }
  }, [user]);

  // Generate a consistent guest ID
  const getGuestId = (): string => {
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
      console.log('Generated new guest ID:', guestId);
    } else {
      console.log('Using existing guest ID:', guestId);
    }
    return guestId;
  };

  // Get user ID if logged in
  const getUserId = (): number | null => {
    return user?.id || null;
  };

  // ============================================================
  // FIXED: fetchProductsFromDatabase - KEEPS all valid images
  // ============================================================
  const fetchProductsFromDatabase = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      
      console.log('Fetching products from:', `${API_BASE_URL}/products/index.php`);
      const response = await fetch(`${API_BASE_URL}/products/index.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Products API response:', result);
      
      if (result.success && Array.isArray(result.data)) {
        if (result.data.length > 0) {
          const normalizedProducts = result.data.map((item: any) => {
            // Get image from all possible sources
            const imageSource = item.image_url || item.image || item.mainImage || item.product_image || '';
            const resolvedImage = getCleanImageUrl(imageSource);
            
            const hoverSource = item.hover_image || item.hoverImage || '';
            const resolvedHover = getCleanImageUrl(hoverSource);
            
            // Process gallery images
            let resolvedGallery: string[] = [];
            const gallerySource = item.galleryImages || item.gallery_images;
            if (Array.isArray(gallerySource)) {
              resolvedGallery = gallerySource
                .map((img: string) => getCleanImageUrl(img))
                .filter(Boolean);
            } else if (typeof gallerySource === 'string' && gallerySource.length > 20) {
              try {
                const parsed = JSON.parse(gallerySource);
                if (Array.isArray(parsed)) {
                  resolvedGallery = parsed
                    .map((img: string) => getCleanImageUrl(img))
                    .filter(Boolean);
                }
              } catch (e) {
                resolvedGallery = gallerySource
                  .split(',')
                  .map((img: string) => getCleanImageUrl(img.trim()))
                  .filter(Boolean);
              }
            }
            
            console.log(`Product ${item.id} - ${item.name}: image = ${resolvedImage?.substring(0, 80)}...`);
            
            return {
              id: item.id,
              name: item.name || '',
              description: item.description || '',
              price: typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0),
              image: resolvedImage || '',
              image_url: resolvedImage || '',
              hoverImage: resolvedHover || '',
              hover_image: resolvedHover || '',
              galleryImages: resolvedGallery,
              gallery_images: resolvedGallery,
              category: item.category || 'jewelry',
              isNew: item.is_new === 1 || item.isNew === true,
              isBestSeller: item.is_best_seller === 1 || item.isBestSeller === true,
              condition: item.condition || 'Brand New',
              weight: item.weight || '',
              karat: item.karat || '',
              goldType: item.gold_type || item.goldType || item.type || 'Standard',
              stock: typeof item.stock === 'string' ? parseInt(item.stock) : (item.stock || 0),
              sku: item.sku || '',
              slug: item.slug || '',
              short_description: item.short_description || item.description || '',
              compare_at_price: item.compare_at_price || 0,
              is_featured: item.is_featured === 1 || item.isFeatured === true,
              status: item.status || 'active',
              views_count: item.views_count || 0,
              created_at: item.created_at || '',
              updated_at: item.updated_at || '',
              is_promo: item.is_promo === 1 || item.is_promo === true,
              promo_price: item.promo_price ? parseFloat(item.promo_price) : null,
              promo_percentage: item.promo_percentage ? parseInt(item.promo_percentage) : null,
              promo_label: item.promo_label || null,
              promo_start_date: item.promo_start_date || null,
              promo_end_date: item.promo_end_date || null,
              isPreOrder: item.isPreOrder === 1 || item.isPreOrder === true,
              preOrderReleaseDate: item.preOrderReleaseDate || null,
              priceOverridden: item.priceOverridden === 1 || item.priceOverridden === true
            };
          });
          
          setProducts(normalizedProducts);
          console.log(`${normalizedProducts.length} products loaded successfully`);
          console.log(`First product image: ${normalizedProducts[0]?.image?.substring(0, 80)}`);
          
          if (result.source) {
            setDataSources(prev => ({ ...prev, products: result.source }));
          }
        } else {
          console.log('No products found in API response');
        }
      } else {
        console.warn('Unexpected API response format:', result);
      }
    } catch (error: unknown) {
      let errorMessage = 'Failed to load products';
      if (error instanceof Error) {
        errorMessage = `Failed to load products: ${error.message}`;
        console.error('Error fetching products:', error.message);
      }
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  // ============================================================
  // FIXED: loadWishlistFromDatabase - PRESERVES all image formats including base64
  // ============================================================
  const loadWishlistFromDatabase = async () => {
    const userId = getUserId();
    const guestId = getGuestId();
    
    console.log('===== LOADING WISHLIST =====');
    
    try {
      const url = userId 
        ? `${API_BASE_URL}/wishlist/get.php?user_id=${userId}&_=${Date.now()}`
        : `${API_BASE_URL}/wishlist/get.php?guest_id=${guestId}&_=${Date.now()}`;
      
      console.log('Fetching wishlist from:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('Raw wishlist API response:', data);
      
      if (data.success && data.data && data.data.length > 0) {
        console.log('Found', data.data.length, 'items in wishlist');
        
        const wishlistItems: WishlistItem[] = data.data.map((item: any) => {
          // CRITICAL: Try ALL possible image sources in priority order
          let imageUrl = '';
          
          // Priority 1: product_image_url from joined products table (MOST RELIABLE)
          if (item.product_image_url && typeof item.product_image_url === 'string' && item.product_image_url.trim() !== '') {
            imageUrl = item.product_image_url;
            console.log(`Item ${item.product_id}: Using product_image_url`);
          }
          // Priority 2: image_url field
          else if (item.image_url && typeof item.image_url === 'string' && item.image_url.trim() !== '') {
            imageUrl = item.image_url;
            console.log(`Item ${item.product_id}: Using image_url`);
          }
          // Priority 3: product_image from wishlist snapshot
          else if (item.product_image && typeof item.product_image === 'string' && item.product_image.trim() !== '') {
            imageUrl = item.product_image;
            console.log(`Item ${item.product_id}: Using product_image`);
          }
          // Priority 4: image field
          else if (item.image && typeof item.image === 'string' && item.image.trim() !== '') {
            imageUrl = item.image;
            console.log(`Item ${item.product_id}: Using image`);
          }
          // Priority 5: db_image_url (legacy)
          else if (item.db_image_url && typeof item.db_image_url === 'string' && item.db_image_url.trim() !== '') {
            imageUrl = item.db_image_url;
            console.log(`Item ${item.product_id}: Using db_image_url`);
          }
          // Priority 6: db_image (legacy)
          else if (item.db_image && typeof item.db_image === 'string' && item.db_image.trim() !== '') {
            imageUrl = item.db_image;
            console.log(`Item ${item.product_id}: Using db_image`);
          }
          
          // Use getCleanImageUrl to KEEP all valid images (base64, blob, http)
          const resolvedImage = getCleanImageUrl(imageUrl);
          console.log(`Final image for item ${item.product_id}: ${resolvedImage ? resolvedImage.substring(0, 80) : 'EMPTY'}...`);
          
          return {
            id: item.product_id || item.id,
            wishlist_id: item.id,
            name: item.product_name || item.name || item.db_name || 'Unknown Product',
            price: parseFloat(item.product_price || item.price) || 0,
            image: resolvedImage,
            image_url: resolvedImage,
            category: item.product_category || item.category || '',
            description: item.product_description || item.description || '',
            stock: item.stock || 0,
            condition: item.condition || 'Brand New',
            karat: item.karat || '',
            goldType: item.goldType || '',
            weight: item.weight || '',
            isNew: item.isNew || false,
            isBestSeller: item.isBestSeller || false,
            is_promo: item.is_promo || false,
            promo_price: item.promo_price || null,
            promo_percentage: item.promo_percentage || null,
            promo_label: item.promo_label || null,
            promo_end_date: item.promo_end_date || null,
            quantity: item.quantity || 1,
            selected: item.selected === 1 || false
          };
        });
        
        setWishlist(wishlistItems);
        console.log(`Successfully loaded ${wishlistItems.length} wishlist items with images`);
      } else {
        console.log('No wishlist items found');
        setWishlist([]);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      setWishlist([]);
    }
  };

  // ============================================================
  // FIXED: loadCartFromDatabase - PRESERVES all image formats
  // ============================================================
  const loadCartFromDatabase = async () => {
    const userId = getUserId();
    const guestId = getGuestId();
    
    console.log('🛒 ===== LOADING CART =====');
    
    try {
      const url = userId 
        ? `${API_BASE_URL}/cart/get.php?user_id=${userId}&_=${Date.now()}`
        : `${API_BASE_URL}/cart/get.php?guest_id=${guestId}&_=${Date.now()}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        console.log('🛒 Found', data.data.length, 'items in cart');
        
        const cartItems: CartItem[] = data.data.map((item: any) => {
          let imageUrl = '';
          
          // Priority 1: product_image_url from joined products table
          if (item.product_image_url && typeof item.product_image_url === 'string' && item.product_image_url.trim() !== '') {
            imageUrl = item.product_image_url;
          }
          // Priority 2: image_url field
          else if (item.image_url && typeof item.image_url === 'string' && item.image_url.trim() !== '') {
            imageUrl = item.image_url;
          }
          // Priority 3: product_image from cart snapshot
          else if (item.product_image && typeof item.product_image === 'string' && item.product_image.trim() !== '') {
            imageUrl = item.product_image;
          }
          // Priority 4: image field
          else if (item.image && typeof item.image === 'string' && item.image.trim() !== '') {
            imageUrl = item.image;
          }
          // Priority 5: db_image_url (legacy)
          else if (item.db_image_url && typeof item.db_image_url === 'string' && item.db_image_url.trim() !== '') {
            imageUrl = item.db_image_url;
          }
          // Priority 6: db_image (legacy)
          else if (item.db_image && typeof item.db_image === 'string' && item.db_image.trim() !== '') {
            imageUrl = item.db_image;
          }
          
          const resolvedImage = getCleanImageUrl(imageUrl);
          
          return {
            id: item.product_id || item.id,
            cart_id: item.id,
            name: item.product_name || item.name || item.db_name || 'Unknown Product',
            price: parseFloat(item.product_price || item.price) || 0,
            image: resolvedImage,
            image_url: resolvedImage,
            category: item.product_category || item.category || '',
            description: item.product_description || item.description || '',
            quantity: parseInt(item.quantity) || 1,
            selected: item.selected === 1 || false,
            stock: item.stock || 0,
            total: (parseFloat(item.product_price || item.price) || 0) * (parseInt(item.quantity) || 1)
          };
        });
        
        setCart(cartItems);
        console.log(`Successfully loaded ${cartItems.length} cart items`);
      } else {
        console.log('No cart items found');
        setCart([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart([]);
    }
  };

  // Sync wishlist with database
  const syncWishlistWithDatabase = async (product: Product, action: 'add' | 'remove'): Promise<boolean> => {
    const userId = getUserId();
    const guestId = getGuestId();
    
    try {
      const endpoint = action === 'add' ? 'add.php' : 'remove.php';
      console.log(`Syncing wishlist ${action} for product ${product.id}`);
      
      // Get clean image URL - PRESERVE the actual image
      const cleanImageUrl = getCleanImageUrl(product.image);
      
      const requestBody: any = {
        user_id: userId,
        guest_id: guestId,
        product_id: product.id
      };
      
      if (action === 'add') {
        requestBody.product_data = {
          name: product.name,
          price: product.price,
          description: product.description || '',
          category: product.category || '',
          image_url: cleanImageUrl,
          image: cleanImageUrl,
          stock: product.stock || 0,
          condition: product.condition || 'Brand New',
          karat: product.karat || '',
          goldType: product.goldType || '',
          weight: product.weight || '',
          isNew: product.isNew || false,
          isBestSeller: product.isBestSeller || false,
          is_promo: (product as any).is_promo || false,
          promo_price: (product as any).promo_price || null,
          isPreOrder: (product as any).isPreOrder || false
        };
      }
      
      const response = await fetch(`${API_BASE_URL}/wishlist/${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) return false;
      
      const data = await response.json();
      
      if (data.success) {
        await loadWishlistFromDatabase();
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error syncing wishlist:', error);
      return false;
    }
  };

  // Bulk remove wishlist items
  const bulkRemoveWishlistItems = async (productIds: number[]): Promise<boolean> => {
    const userId = getUserId();
    const guestId = getGuestId();
    
    if (!productIds.length) return true;
    
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/remove-bulk.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_ids: productIds
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await loadWishlistFromDatabase();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error bulk removing wishlist items:', error);
      return false;
    }
  };

  // Sync cart with database
  const syncCartWithDatabase = async (product: Product, quantity: number, action: 'add' | 'update' | 'remove'): Promise<boolean> => {
    const userId = getUserId();
    const guestId = getGuestId();
    
    try {
      let response;
      let endpoint = '';
      
      if (action === 'remove') {
        endpoint = 'remove.php';
        response = await fetch(`${API_BASE_URL}/cart/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            guest_id: guestId,
            product_id: product.id
          })
        });
      } else if (action === 'update') {
        endpoint = 'update-quantity.php';
        response = await fetch(`${API_BASE_URL}/cart/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            guest_id: guestId,
            product_id: product.id,
            quantity: quantity
          })
        });
      } else {
        endpoint = 'add.php';
        const cleanImageUrl = getCleanImageUrl(product.image);
        
        response = await fetch(`${API_BASE_URL}/cart/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            guest_id: guestId,
            product_id: product.id,
            product_data: {
              name: product.name,
              price: product.price,
              description: product.description || '',
              category: product.category || '',
              image_url: cleanImageUrl,
              image: cleanImageUrl,
              stock: product.stock || 0,
              condition: product.condition || 'Brand New'
            },
            quantity: quantity
          })
        });
      }
      
      if (!response.ok) return false;
      
      const data = await response.json();
      
      if (data.success && (action === 'add' || action === 'remove')) {
        setTimeout(() => {
          loadCartFromDatabase();
        }, 100);
      }
      
      return data.success === true;
    } catch (error) {
      console.error('Error syncing cart:', error);
      return false;
    }
  };
  
  // Fetch gold rates from database
  const fetchGoldRatesFromDatabase = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/gold-rates/index.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        if (result.data.length > 0) {
          const mappedRates: GoldRate[] = result.data.map((item: any) => ({
            id: item.id,
            goldType: item.goldType || item.gold_type,
            karat: item.karat,
            pricePerGram: item.pricePerGram || item.price_per_gram,
            lastUpdated: item.last_updated || item.updated_at
          }));
          
          setRates(mappedRates);
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error fetching gold rates:', error.message);
      }
    }
  };

  // Fetch site settings from database
  const fetchSiteSettingsFromDatabase = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/index.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        let settingsObj: Record<string, any> = {};
        
        if (Array.isArray(result.data)) {
          settingsObj = (result.data as SettingsItem[]).reduce((acc: Record<string, any>, item: SettingsItem) => {
            return { ...acc, [item.setting_key]: item.setting_value };
          }, {});
        } else if (typeof result.data === 'object') {
          settingsObj = result.data as Record<string, any>;
        }
        
        setSiteSettings({ ...DEFAULT_SITE_SETTINGS, ...settingsObj });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error fetching site settings:', error.message);
      }
    }
  };

  // Fetch members from database
  const fetchMembersFromDatabase = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/members/index.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        if (result.data.length > 0) {
          const filteredMembers = result.data
            .filter((m: any) => m.role !== 'admin')
            .map((m: any) => {
              let points = m.points || 0;
              const totalSpent = m.total_spent || m.totalSpent || 0;
              
              if (points === 0 && totalSpent > 0) {
                points = calculatePointsFromSpent(totalSpent);
              }
              
              const tier = getTierFromPoints(points);
              
              return {
                id: m.id,
                name: m.name || m.first_name + ' ' + m.last_name || 'Member',
                email: m.email,
                phone: m.phone || '',
                avatar: m.avatar || '',
                role: m.role || 'member',
                membership_tier: tier,
                points: points,
                status: m.status || 'Active',
                totalSpent: totalSpent,
                ordersCount: m.orders_count || m.ordersCount || 0,
                joinDate: m.joinDate || m.created_at?.split('T')[0] || new Date().toLocaleDateString(),
                created_at: m.created_at,
                last_login: m.last_login
              };
            });
          
          setMembers(filteredMembers);
        } else {
          setMembers([]);
        }
      } else {
        setMembers([]);
      }
    } catch (error: unknown) {
      console.error('Error fetching members:', error);
      setMembers([]);
    }
  };

  // Fetch orders from database
  const fetchOrdersFromDatabase = async () => {
    try {
      const userId = getUserId();
      
      let url = `${API_BASE_URL}/orders/get.php`;
      
      if (userId) {
        url = `${API_BASE_URL}/orders/get.php?user_id=${userId}`;
      } else if (user?.role === 'admin') {
        url = `${API_BASE_URL}/orders/get.php?admin=true`;
      } else {
        setAllOrders([]);
        return;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.orders)) {
        const mappedOrders = result.orders.map((order: any) => ({
          ...order,
          deliveryType: order.deliveryType || order.delivery_type || 'shipping'
        }));
        setAllOrders(mappedOrders);
      } else if (result.success && Array.isArray(result.data)) {
        const mappedOrders = result.data.map((order: any) => ({
          ...order,
          deliveryType: order.deliveryType || order.delivery_type || 'shipping'
        }));
        setAllOrders(mappedOrders);
      } else {
        setAllOrders([]);
      }
    } catch (error: unknown) {
      console.error('Error fetching orders:', error);
      setAllOrders([]);
    }
  };

  // Refresh products after add/edit/delete
  const refreshProducts = async () => {
    setIsLoading(true);
    await fetchProductsFromDatabase();
    setIsLoading(false);
  };

  // Retry fetching all data
  const handleRetry = () => {
    fetchProductsFromDatabase();
    fetchGoldRatesFromDatabase();
    fetchSiteSettingsFromDatabase();
    fetchMembersFromDatabase();
    fetchOrdersFromDatabase();
    loadWishlistFromDatabase();
    loadCartFromDatabase();
  };

  // Admin URL Detection Logic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;

    if (
        params.get('entry') === 'staff' || 
        path === '/admin' || path.endsWith('/admin') ||
        path === '/admin-login' || path.endsWith('/admin-login') ||
        path === '/login-admin' || path.endsWith('/login-admin')
    ) {
        setActivePage('admin-portal');
    }
  }, []);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activePage]);

  // Handle user update from MemberProfile
  const handleUpdateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('sparklingUser', JSON.stringify(updatedUser));
  };

  const handleLogin = (userData: User) => {
    setUser(userData);
    setActivePage('home');
    
    setTimeout(() => {
      loadWishlistFromDatabase();
      loadCartFromDatabase();
      fetchOrdersFromDatabase();
    }, 500);
  };

  const handleLogout = () => {
    setUser(null);
    setActivePage('home');
    setAllOrders([]);
    
    localStorage.removeItem('guest_id');
    setTimeout(() => {
      loadWishlistFromDatabase();
      loadCartFromDatabase();
    }, 500);
    
    try {
        const url = new URL(window.location.href);
        const path = window.location.pathname;
        
        if (url.searchParams.has('entry')) {
            url.searchParams.delete('entry');
        }
        
        if (
            path.endsWith('/admin') || 
            path.endsWith('/admin-login') || 
            path.endsWith('/login-admin')
        ) {
            window.history.pushState({}, '', '/');
        } else {
            window.history.pushState({}, '', url.toString());
        }
    } catch (e) {
        console.debug("Logout history update skipped:", e);
    }
  };

  // Product Management Handlers
  const handleAddProduct = async (newProduct: Product) => {
    setProducts(prev => [...prev, newProduct]);
    await refreshProducts();
  };

  const handleEditProduct = async (updatedProduct: Product) => {
    setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    await refreshProducts();
  };

  const handleDeleteProduct = async (id: number) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    await refreshProducts();
  };

  // Rate Management Handler
  const handleUpdateRates = (newRates: GoldRate[]) => {
    setRates(newRates);
    
    const updatedProducts = products.map(product => {
      if (product.category === 'jewelry' && product.weight && product.goldType && product.karat) {
        const weightVal = parseWeight(product.weight);
        
        const matchingRate = newRates.find(rate => 
          rate.goldType === product.goldType && 
          rate.karat === product.karat
        );
        
        if (!isNaN(weightVal) && weightVal > 0 && matchingRate) {
          const newPrice = weightVal * matchingRate.pricePerGram;
          
          const isPriceOverridden = (product as any).priceOverridden === true;
          if (!isPriceOverridden) {
            return { ...product, price: newPrice };
          }
        }
      }
      return product;
    });
    
    setProducts(updatedProducts);
  };

  const handleUpdateSettings = (newSettings: SiteSettings) => {
      setSiteSettings(newSettings);
  };

  // Order Logic
  const handlePlaceOrder = async (newOrder: MemberOrder) => {
    setAllOrders(prev => [newOrder, ...prev]);
    
    for (const item of newOrder.items) {
      if (item.quantity > 0) {
        try {
          const response = await fetch(`${API_BASE_URL}/products/update-stock.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              product_id: item.id, 
              quantity: item.quantity, 
              operation: 'decrease' 
            })
          });
          await response.json();
        } catch (error) {
          console.error(`Error updating stock for product ${item.id}:`, error);
        }
      }
    }
    
    const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
    
    if (wishlistCheckout) {
      try {
        const data = JSON.parse(wishlistCheckout);
        
        if (data.source === 'wishlist' && data.items && data.items.length > 0) {
          const checkedOutProductIds = data.items.map((item: any) => item.product.id);
          await bulkRemoveWishlistItems(checkedOutProductIds);
          sessionStorage.removeItem('wishlistCheckout');
          await loadWishlistFromDatabase();
        }
      } catch (error) {
        console.error('Error removing wishlist items after checkout:', error);
      }
    }
    
    await refreshProducts();
    
    setCart([]);
    setDirectCheckoutItem(null);
    setWishlistCheckoutItems(null);
    sessionStorage.removeItem('directCheckout');
    fetchOrdersFromDatabase();
  };

  const handleUpdateOrder = (orderId: string, updates: Partial<MemberOrder>) => {
      setAllOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, ...updates } : order
      ));
  };

  // Wishlist Logic
  const toggleWishlist = async (product: Product) => {
    const productId = product.id;

    if (wishlistProcessingRef.current.has(productId)) {
      return;
    }

    wishlistProcessingRef.current.add(productId);

    try {
      const exists = wishlist.some(item => item.id === productId);
      const action: 'add' | 'remove' = exists ? 'remove' : 'add';

      const success = await syncWishlistWithDatabase(product, action);

      if (!success) {
        alert('Failed to update wishlist. Please try again.');
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      alert('Failed to update wishlist. Please try again.');
    } finally {
      wishlistProcessingRef.current.delete(productId);
    }
  };

  const isInWishlist = (id: number) => {
      return wishlist.some(item => item.id === id);
  };

  const updateWishlistQuantity = (id: number, delta: number) => {
    setWishlist(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const toggleWishlistSelection = (id: number) => {
    setWishlist(prev => prev.map(item => 
      item.id === id ? { ...item, selected: !item.selected } : item
    ));
  };

  const toggleAllWishlistSelection = (select: boolean) => {
    setWishlist(prev => prev.map(item => ({ ...item, selected: select })));
  };

  const removeFromWishlist = async (ids: number[]) => {
    const itemsToRemove = wishlist.filter(item => ids.includes(item.id));
    
    let success = true;
    for (const item of itemsToRemove) {
      const result = await syncWishlistWithDatabase(item as Product, 'remove');
      if (!result) success = false;
    }
    
    if (success) {
      await loadWishlistFromDatabase();
    } else {
      alert('Failed to remove items. Please try again.');
    }
  };

  // Cart Logic
  const handleAddToCart = async (product: Product, quantity: number): Promise<boolean> => {
    if (product.stock !== undefined && product.stock <= 0) {
      alert('Sorry, this item is out of stock.');
      return false;
    }
    
    if (product.stock !== undefined && quantity > product.stock) {
      alert(`Sorry, only ${product.stock} items available in stock.`);
      return false;
    }
    
    try {
      const success = await syncCartWithDatabase(product, quantity, 'add');
      
      if (success) {
        return true;
      } else {
        alert('Failed to add to cart. Please try again.');
        return false;
      }
    } catch (error) {
      alert('Connection error. Please check your network and try again.');
      return false;
    }
  };

  const handleRemoveFromCart = async (productId: number) => {
    const product = cart.find(item => item.id === productId);
    if (!product) return;
    
    try {
      const success = await syncCartWithDatabase(product as Product, 0, 'remove');
      
      if (success) {
        await loadCartFromDatabase();
      } else {
        alert('Failed to remove item. Please try again.');
      }
    } catch (error) {
      alert('Connection error. Please try again.');
    }
  };

  const handleUpdateCartQty = async (productId: number, delta: number) => {
    const currentItem = cart.find(item => item.id === productId);
    if (!currentItem) return;
    
    const newQuantity = Math.max(1, currentItem.quantity + delta);
    
    if (currentItem.stock !== undefined && newQuantity > currentItem.stock) {
      alert(`Sorry, only ${currentItem.stock} items available in stock.`);
      return;
    }
    
    try {
      const success = await syncCartWithDatabase(currentItem as Product, newQuantity, 'update');
      
      if (success) {
        await loadCartFromDatabase();
      } else {
        alert('Failed to update quantity. Please try again.');
      }
    } catch (error) {
      alert('Connection error. Please try again.');
    }
  };

  const moveToCart = async (ids: number[]) => {
    const itemsToMove = wishlist.filter(item => ids.includes(item.id));
    
    let allSuccessful = true;
    for (const item of itemsToMove) {
      const result = await handleAddToCart(item as Product, 1);
      if (!result) {
        allSuccessful = false;
        break;
      }
    }
    
    if (allSuccessful) {
      await removeFromWishlist(ids);
    } else {
      alert('Failed to move some items. Please try again.');
      loadWishlistFromDatabase();
      loadCartFromDatabase();
    }
  };

  const handleOpenCart = () => {
    setShowCart(true);
  };

  const handleOpenWishlist = () => {
    setShowWishlist(true);
  };

  const handleCloseCart = () => {
    setShowCart(false);
  };

  const handleCloseWishlist = () => {
    setShowWishlist(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-sans selection:bg-gold-200 selection:text-stone-900">
      {/* Global Product Modal */}
      {selectedProduct && (
        <ProductDetailModal 
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
          onToggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
          onNavigate={(page) => {
            if (page === 'checkout' && selectedProduct) {
              const checkoutData = {
                product: selectedProduct,
                quantity: 1
              };
              sessionStorage.setItem('directCheckout', JSON.stringify(checkoutData));
              setDirectCheckoutItem(checkoutData);
            }
            setActivePage(page);
          }}
        />
      )}

      {/* Wishlist Overlay */}
      {showWishlist && (
        <Wishlist
          onNavigate={setActivePage}
          onSelectProduct={setSelectedProduct}
          user={user}
          onClose={handleCloseWishlist}
          onWishlistCheckout={(items) => {
            // Store checkout data but DO NOT remove items yet
            const checkoutData = {
              items: items,
              source: 'wishlist',
              timestamp: Date.now()
            };
            sessionStorage.setItem('wishlistCheckout', JSON.stringify(checkoutData));
            // Items will be removed after successful checkout in handlePlaceOrder
            // const productIds = items.map(item => item.product.id);
            // bulkRemoveWishlistItems(productIds); // REMOVED - items should only be removed after successful order
            setActivePage('checkout');
          }}
        />
      )}

      {/* Navbar */}
      {activePage !== 'auth' && activePage !== 'admin-portal' && activePage !== 'register' && (
        <Navbar 
            onNavigate={setActivePage} 
            user={user} 
            onLogout={handleLogout} 
            activePage={activePage}
            wishlist={wishlist}
            onOpenWishlist={handleOpenWishlist}
        />
      )}
      
      <main>
        {activePage === 'home' && (
          <div className="page-transition">
            <Hero onNavigate={setActivePage} user={user} />
            
            <div className="w-full h-16 bg-gradient-to-b from-stone-50/0 to-stone-50"></div>
            
            <div id="collections">
              <FeaturedCollection onNavigate={setActivePage} />
            </div>
            
            <div id="shop" className="py-20 bg-white">
              <div className="text-center mb-12">
                <span className="text-gold-600 text-[10px] tracking-[0.3em] uppercase font-bold">Curated For You</span>
                <h2 className="text-3xl font-serif text-stone-900 mt-3">Browse Categories</h2>
              </div>
              <GridCategories onNavigate={setActivePage} />
            </div>

            <div id="maison">
              <VideoSection onNavigate={setActivePage} />
            </div>

            {apiError && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 mx-4 mb-4 rounded-sm">
                <p className="text-sm">{apiError}</p>
                <p className="text-xs mt-1">Showing sample products</p>
                <button 
                  onClick={handleRetry}
                  className="text-xs underline mt-2 hover:text-yellow-600"
                >
                  Retry Connection
                </button>
                {dataSources.products && (
                  <p className="text-xs mt-1 text-stone-500">
                    Using {dataSources.products} data
                  </p>
                )}
              </div>
            )}

            {rates.length > 0 && (
              <div className="bg-stone-100 py-6 overflow-hidden whitespace-nowrap border-y border-stone-200">
                <div className="inline-block animate-[scroll_20s_linear_infinite] text-stone-600 text-xs tracking-[0.5em] uppercase">
                  {rates.map((rate, index) => (
                    <span key={index}>
                      {rate.goldType} {rate.karat}: ₱{rate.pricePerGram}/g •{' '}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Shop Pages */}
        {activePage === 'shop' && (
          <Shop 
            initialCategory="all"
            onNavigate={setActivePage}
            products={products}
            wishlist={wishlist}
            onToggleWishlist={toggleWishlist}
            onAddToCart={handleAddToCart}
            onSelectProduct={setSelectedProduct}
            user={user}
          />
        )}
        
        {activePage === 'collections' && (
          <Shop 
            initialCategory="jewelry"
            onNavigate={setActivePage}
            products={products}
            wishlist={wishlist}
            onToggleWishlist={toggleWishlist}
            onAddToCart={handleAddToCart}
            onSelectProduct={setSelectedProduct}
            user={user}
          />
        )}
        
        {activePage === 'bags' && (
          <Shop 
            initialCategory="bags"
            onNavigate={setActivePage}
            products={products}
            wishlist={wishlist}
            onToggleWishlist={toggleWishlist}
            onAddToCart={handleAddToCart}
            onSelectProduct={setSelectedProduct}
            user={user}
          />
        )}
        
        {activePage === 'fragrances' && (
          <Shop 
            initialCategory="fragrances"
            onNavigate={setActivePage}
            products={products}
            wishlist={wishlist}
            onToggleWishlist={toggleWishlist}
            onAddToCart={handleAddToCart}
            onSelectProduct={setSelectedProduct}
            user={user}
          />
        )}

        {/* Checkout Page */}
        {activePage === 'checkout' && (
          <Checkout 
              cart={wishlistCheckoutItems || cart}
              onPlaceOrder={handlePlaceOrder}
              onNavigate={setActivePage}
              onSelectProduct={setSelectedProduct}
              onUpdateCartQty={handleUpdateCartQty}
              onRemoveFromCart={handleRemoveFromCart}
              onClearCart={() => {
                setCart([]);
                setWishlistCheckoutItems(null);
              }}
              directCheckoutItem={directCheckoutItem}
              user={user}
              onRefreshWishlist={loadWishlistFromDatabase}
          />
        )}

        {/* Payment Proof Upload */}
        {activePage === 'upload-proof' && (
          <PaymentProofUpload />
        )}

        {/* Other Pages */}
        {activePage === 'contact' && <Contact settings={siteSettings} />}
        {activePage === 'services' && <Services onNavigate={setActivePage} />}
        {activePage === 'about' && <About onNavigate={setActivePage} settings={siteSettings} />}
        {activePage === 'privacy-policy' && <PrivacyPolicy onNavigate={setActivePage} />}
        {activePage === 'auth' && <Auth onNavigate={setActivePage} onLogin={handleLogin} />}
        {activePage === 'register' && <Auth onNavigate={setActivePage} onLogin={handleLogin} initialMode="register" />}
        {activePage === 'admin-portal' && <Auth onNavigate={setActivePage} onLogin={handleLogin} initialAdmin={true} />}
        {activePage === 'reset-password' && <ResetPassword onNavigate={setActivePage} />}
        
        {activePage === 'profile' && user && (
          <MemberProfile 
              user={user} 
              onNavigate={setActivePage} 
              onLogout={handleLogout}
              onUpdateUser={handleUpdateUser}
              initialTab='overview'
              wishlist={wishlist}
              onUpdateWishlistQuantity={updateWishlistQuantity}
              onToggleWishlistSelection={toggleWishlistSelection}
              onToggleAllWishlistSelection={toggleAllWishlistSelection}
              onRemoveFromWishlist={removeFromWishlist}
              onMoveToCart={moveToCart}
              onSelectProduct={setSelectedProduct}
              orders={allOrders}
              onUpdateOrder={handleUpdateOrder}
          />
        )}
        
        {activePage === 'orders' && user && (
          <MemberProfile 
              user={user} 
              onNavigate={setActivePage} 
              onLogout={handleLogout}
              onUpdateUser={handleUpdateUser}
              initialTab='orders'
              wishlist={wishlist}
              onUpdateWishlistQuantity={updateWishlistQuantity}
              onToggleWishlistSelection={toggleWishlistSelection}
              onToggleAllWishlistSelection={toggleAllWishlistSelection}
              onRemoveFromWishlist={removeFromWishlist}
              onMoveToCart={moveToCart}
              onSelectProduct={setSelectedProduct}
              orders={allOrders}
              onUpdateOrder={handleUpdateOrder}
          />
        )}

        {/* Admin Pages */}
        {activePage === 'admin-dashboard' && <AdminDashboard onNavigate={setActivePage} user={user} />}
        
        {(activePage === 'admin-products' || activePage === 'admin-add-product') && (
          <AdminProducts 
              products={products}
              rates={rates} 
              onAdd={handleAddProduct} 
              onEdit={handleEditProduct} 
              onDelete={handleDeleteProduct} 
              onNavigate={setActivePage} 
              initialView={activePage === 'admin-add-product' ? 'form' : 'list'}
              onRefresh={refreshProducts}
          />
        )}

        {activePage === 'admin-pricing' && (
          <AdminPricing 
              rates={rates}
              onUpdateRates={handleUpdateRates}
              onNavigate={setActivePage}
          />
        )}
        
        {activePage === 'admin-profile' && <AdminProfile onNavigate={setActivePage} />}
        
        {activePage === 'admin-settings' && (
          <AdminSettings 
              settings={siteSettings}
              onUpdateSettings={handleUpdateSettings}
              onNavigate={setActivePage}
          />
        )}

        {activePage === 'admin-members' && (
          <AdminMembers 
              members={members}
              onNavigate={setActivePage}
          />
        )}

        {activePage === 'admin-transactions' && (
          <AdminTransactions 
              orders={allOrders}
              onUpdateOrder={handleUpdateOrder}
              onNavigate={setActivePage}
          />
        )}

        {activePage === 'admin-bank-details' && (
          <AdminBankDetails onNavigate={setActivePage} />
        )}

      </main>

      {/* Footer */}
      {activePage !== 'auth' && activePage !== 'admin-portal' && activePage !== 'register' && (
        <>
          <Footer settings={siteSettings} onNavigate={setActivePage} user={user} />
          <Concierge />
          <ScrollToTop />
        </>
      )}
    </div>
  );
}

export default App;